import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
} from '@chakra-ui/react';
import assert from 'assert';
import { getIn, setIn } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import {
  createLinkedAccountPropertyShell,
  getPropertyShell,
} from '@finley-technologies/credit-agreement-utils';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { Maybe } from 'types/dashboard-core-gqlTypes.generated';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import {
  formatSelectOptions,
  Select,
} from 'packages/common-frontend/components/Select';
import {
  isPropertyContainerType,
  isPropertyType,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

export const createLinkedAccountPropertyContainerShell = (
  propertyKey: string,
): CreditAgreement.DomainEntity.LinkedAccountPropertyContainer => ({
  name: _.startCase(propertyKey),
  type:
    CreditAgreement.DomainEntity.PropertyContainerType
      .LinkedAccountPropertyContainer,
  contents: [createLinkedAccountPropertyShell()],
  _isWrittenIn_: true,
});

export const createReportTypePropertyContainerShell = (
  propertyKey: string,
): CreditAgreement.DomainEntity.ReportTypePropertyContainer => ({
  name: _.startCase(propertyKey),
  type:
    CreditAgreement.DomainEntity.PropertyContainerType
      .ReportTypePropertyContainer,
  contents: [],
  _isWrittenIn_: true,
});

export const createTextPropertyContainerShell = (
  propertyKey: string,
): CreditAgreement.DomainEntity.TextPropertyContainer => ({
  name: _.startCase(propertyKey),
  contents: [],
  type:
    CreditAgreement.DomainEntity.PropertyContainerType.TextPropertyContainer,
  _isWrittenIn_: true,
});

export const getPropertyContainerShell = ({
  type,
  propertyKey,
}: {
  type: CreditAgreement.DomainEntity.PropertyContainerType;
  propertyKey: string;
}) => {
  switch (type) {
    case CreditAgreement.DomainEntity.PropertyContainerType
      .LinkedAccountPropertyContainer:
      return createLinkedAccountPropertyContainerShell(propertyKey);
    case CreditAgreement.DomainEntity.PropertyContainerType
      .ReportTypePropertyContainer:
      return createReportTypePropertyContainerShell(propertyKey);
    case CreditAgreement.DomainEntity.PropertyContainerType
      .TextPropertyContainer:
      return createTextPropertyContainerShell(propertyKey);
    case CreditAgreement.DomainEntity.PropertyContainerType
      .DeliverableReportPropertyContainer:
      /**
       *  deliverableReportPropertyContainers will not be added through the AddPropertyModal, and instead be handled by an onClick handler within section/ReportingDeliverables AddIconButton.
       * DeliverableReportPropertyContainers should not be used outside of the ReportingDeliverables section
       */
      throw Error(`unsupported property type ${type}`);
    default:
      return finleyAssertUnreachable(type);
  }
};

type AddPropertyModalProps = {
  basePropertyKey: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  propertyMap: {
    [key: string]:
      | CreditAgreement.DomainEntity.PropertyType
      | CreditAgreement.DomainEntity.PropertyContainerType;
  };
};
export const AddPropertyModal = ({
  basePropertyKey,
  isOpen,
  setIsOpen,
  wipConfig: { formProps, saveCreditAgreementFullArtifactWip },
  propertyMap,
}: AddPropertyModalProps) => {
  const possibleKeys = _.keys(propertyMap);

  const currentKeys = _.map(
    _.filter(
      _.toPairs(getIn(formProps.values, basePropertyKey)),
      ([, value]) => !_.isNil(value),
    ),
    ([key]) => key,
  );

  const selectionKeys = _.difference(possibleKeys, currentKeys);
  const selectionOptions = formatSelectOptions(selectionKeys, 'title');

  const [selectedKey, setSelectedKey] = useState<Maybe<string>>(undefined);

  const createProperty = ({
    type,
    propertyKey,
  }: {
    type:
      | CreditAgreement.DomainEntity.PropertyType
      | CreditAgreement.DomainEntity.PropertyContainerType;
    propertyKey: string;
  }) => {
    let shell:
      | CreditAgreement.DomainEntity.Property
      | CreditAgreement.DomainEntity.PropertyContainer
      | null = null;
    if (isPropertyContainerType(type)) {
      // @ts-ignore
      shell = getPropertyContainerShell({ type, propertyKey });
    }
    if (isPropertyType(type)) {
      shell = getPropertyShell({ type, propertyKey });
    }
    assert(shell, `Expected property shell to exist for ${type}`);
    const propertySection = getIn(formProps.values, basePropertyKey);
    return { ...propertySection, [propertyKey]: shell };
  };

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="3xl">
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>Add Property</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Select
            value={selectedKey}
            onChange={(propertyKey) => setSelectedKey(propertyKey)}
            options={selectionOptions}
            placeholderLabel="Select property"
          />
          <Flex justifyContent="center" mt="8">
            <Button
              onClick={() => {
                if (selectedKey) {
                  const newPropertyValue = createProperty({
                    type: propertyMap[selectedKey],
                    propertyKey: selectedKey,
                  });
                  formProps.setFieldValue(basePropertyKey, newPropertyValue);

                  const updatedWipCreditAgreement = setIn(
                    formProps.values,
                    basePropertyKey,
                    newPropertyValue,
                  );
                  saveCreditAgreementFullArtifactWip({
                    variables: {
                      creditAgreementFullArtifactWip: {
                        id: updatedWipCreditAgreement.id,
                        creditFacilityId:
                          updatedWipCreditAgreement.creditFacilityId,
                        creditAgreement: JSON.stringify(
                          updatedWipCreditAgreement,
                        ),
                      },
                    },
                  });
                }
                setIsOpen(false);
              }}
              aria-label="Done"
              isDisabled={_.keys(formProps.errors).length > 0}
            >
              Done
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
